import React, { useEffect, useState, useCallback } from 'react';
import { useGoalReminders } from '../../hooks';
import GoalReminderNotification from '../GoalReminderNotification';
import { useNavigate } from 'react-router-dom';
import { syncOfflineReminderUpdates } from '../../services/firebase/goalReminder.service';
import logger from '../../utils/logger';

/**
 * Component that initializes and manages goal reminders
 * This should be placed high in the component tree to ensure it's always active
 */
const ReminderInitializer: React.FC = () => {
  const { reminders, dismissReminder, refreshReminders} = useGoalReminders();
  const navigate = useNavigate();
  const [activeReminder, setActiveReminder] = useState<typeof reminders[0] | null>(null);
  const [reminderQueue, setReminderQueue] = useState<typeof reminders>([]);
  const [error, setError] = useState<string | null>(null);
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);
  
  // Track online status for syncing offline updates
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      // Sync offline reminder updates when we come back online
      syncOfflineReminderUpdates()
        .then(() => refreshReminders())
        .catch(err => logger.error('Error syncing after coming online:', err));
    };
    
    const handleOffline = () => {
      setIsOnline(false);
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [refreshReminders]);
  
  // Process reminders and queue them if needed
  useEffect(() => {
    if (reminders.length > 0) {
      if (!activeReminder) {
        // Set the first reminder as active if none is showing
        setActiveReminder(reminders[0]);
        
        // Queue the rest
        if (reminders.length > 1) {
          setReminderQueue(reminders.slice(1));
        }
      } else {
        // Queue any new reminders
        const newReminders = reminders.filter(
          r => !reminderQueue.some(q => q.goal.id === r.goal.id && q.timestamp === r.timestamp) && 
               !(activeReminder.goal.id === r.goal.id && activeReminder.timestamp === r.timestamp)
        );
        
        if (newReminders.length > 0) {
          setReminderQueue(prev => [...prev, ...newReminders]);
        }
      }
    }
  }, [reminders, activeReminder, reminderQueue]);
  
  // Clear error after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [error]);
  
  // Handle reminder dismissal with error handling
  const handleDismiss = useCallback(async () => {
    if (!activeReminder) return;
    
    try {
      // Dismiss in backend
      const result = await dismissReminder(activeReminder.goal.id, activeReminder.timestamp);
      
      if (!result.success) {
        // Show error but still remove from UI to avoid getting stuck
        setError(`Error dismissing reminder: ${result.error}`);
      }
      
      // Remove from UI
      setActiveReminder(null);
      
      // Show next reminder if any
      if (reminderQueue.length > 0) {
        const [next, ...rest] = reminderQueue;
        setTimeout(() => {
          setActiveReminder(next);
          setReminderQueue(rest);
        }, 500);
      }
    } catch (err) {
      // Still remove from UI despite the error to prevent getting stuck
      logger.error('Error in handleDismiss:', err);
      setError('Failed to dismiss reminder');
      setActiveReminder(null);
      
      // Try to show next reminder
      if (reminderQueue.length > 0) {
        const [next, ...rest] = reminderQueue;
        setTimeout(() => {
          setActiveReminder(next);
          setReminderQueue(rest);
        }, 500);
      }
    }
  }, [activeReminder, dismissReminder, reminderQueue]);
  
  // Handle navigation to goal detail with error handling
  const handleGoToGoal = useCallback((goalId: string) => {
    if (!activeReminder) return;
    
    try {
      // Dismiss current reminder
      dismissReminder(activeReminder.goal.id, activeReminder.timestamp)
        .catch(err => {
          logger.error('Error dismissing reminder during navigation:', err);
          // Continue with navigation despite error
        });
      
      // Clear from UI
      setActiveReminder(null);
      
      // Navigate to goal details
      navigate(`/goals/${goalId}`);
      
      // Show next reminder if any after navigation
      if (reminderQueue.length > 0) {
        const [next, ...rest] = reminderQueue;
        setTimeout(() => {
          setActiveReminder(next);
          setReminderQueue(rest);
        }, 1000);
      }
    } catch (err) {
      logger.error('Error in handleGoToGoal:', err);
      setError('Error navigating to goal');
      
      // Still clear UI and try to navigate
      setActiveReminder(null);
      navigate(`/goals/${goalId}`);
    }
  }, [activeReminder, dismissReminder, navigate, reminderQueue]);
  
  return (
    <>
      {/* Error message if needed */}
      {error && (
        <div className="fixed top-0 left-0 right-0 m-4 z-50 bg-red-500 text-white p-2 rounded-md text-center">
          {error}
        </div>
      )}
      
      {/* Network status indicator */}
      {!isOnline && (
        <div className="fixed top-0 left-0 right-0 m-4 mt-12 z-50 bg-yellow-500 text-white p-2 rounded-md text-center">
          You are offline. Reminder settings will sync when you reconnect.
        </div>
      )}
      
      {/* Active reminder */}
      {activeReminder && (
        <div className="fixed top-0 right-0 m-4 z-50 max-w-sm">
          <GoalReminderNotification
            goal={activeReminder.goal}
            onDismiss={handleDismiss}
            onGoToGoal={handleGoToGoal}
          />
        </div>
      )}
    </>
  );
};

export default ReminderInitializer;